import { storageKeys } from "../config";
/**
 * The network a page shows, and every network the session names. Every page
 * loads with `chain.chainId` in its deps, so a late answer for the previous
 * network never reaches the screen. The choice is remembered per browser.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { MakerChain } from "./api";

const STORAGE_KEY = storageKeys.network;

export interface NetworkState {
  /** Every network the session names, in its order. Never empty inside the provider. */
  chains: readonly MakerChain[];
  chain: MakerChain;
  /** Selects a network by key; an unknown key is ignored. */
  select: (key: string) => void;
}

const NetworkContext = createContext<NetworkState | null>(null);

function readStoredKey(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeKey(key: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // Not remembered; the choice lasts until reload.
  }
}

/** Stands in when the session names no network at all: every page then says it cannot read one. */
export const NO_CHAIN: MakerChain = { key: "none", chainId: 0, label: "No network", explorerUrl: null };

export function NetworkProvider({ chains, initialKey, children }: { chains: readonly MakerChain[]; initialKey?: string; children: ReactNode }) {
  const list = useMemo(() => (chains.length > 0 ? chains : [NO_CHAIN]), [chains]);
  const [key, setKey] = useState<string>(() => {
    const wanted = initialKey ?? readStoredKey();
    return wanted !== null && list.some((c) => c.key === wanted) ? wanted : (list[0]?.key ?? NO_CHAIN.key);
  });
  const select = useCallback(
    (next: string) => {
      if (!list.some((c) => c.key === next)) return;
      storeKey(next);
      setKey(next);
    },
    [list],
  );
  const value = useMemo<NetworkState>(() => ({ chains: list, chain: list.find((c) => c.key === key) ?? list[0] ?? NO_CHAIN, select }), [list, key, select]);
  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>;
}

/** The selected network and the list. Throws outside `<NetworkProvider>`. */
export function useNetwork(): NetworkState {
  const state = useContext(NetworkContext);
  if (state === null) throw new Error("useNetwork() outside <NetworkProvider>");
  return state;
}

/** The other networks' labels, in order: "Base", or "Base and BSC". */
export function otherLabels(chains: readonly MakerChain[], chain: MakerChain): string {
  const names = chains.filter((c) => c.key !== chain.key).map((c) => c.label);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]!}`;
}

/** "Base and BSC": every network, in order. */
export function allLabels(chains: readonly MakerChain[]): string {
  const names = chains.map((c) => c.label);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]!}`;
}
